import { useCallback, useEffect, useRef } from 'react'

import { Skeleton } from '@/components/ui'
import { useDocumentPages } from '@/modules/documents'
import { cn } from '@/utils'

interface ThumbnailsProps {
  showThumbnails: boolean
  currentPageIndex: number
  setCurrentPage: (i: number) => void
}
export const Thumbnails = ({
  showThumbnails,
  currentPageIndex,
  setCurrentPage,
}: ThumbnailsProps) => {
  const pages = useDocumentPages()
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  const scrollIntoView = useCallback(() => {
    itemRefs.current[currentPageIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }, [currentPageIndex])

  useEffect(() => {
    if (showThumbnails) {
      const id = setTimeout(() => scrollIntoView(), 100)
      return () => clearTimeout(id)
    }
  }, [scrollIntoView, showThumbnails])

  if (!showThumbnails) return

  const total = pages.length

  return (
    <aside className="w-20 overflow-y-auto rounded-bl-lg border border-r-0 bg-gray-200 flex flex-col gap-2 p-1">
      {total === 0
        ? Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded" />
          ))
        : pages.map((page, i) => (
            <button
              key={page.id}
              ref={(el) => {
                itemRefs.current[i] = el
              }}
              type="button"
              onClick={() => setCurrentPage(i)}
              className={cn(
                `group flex flex-col items-center gap-1 rounded p-0.5 transition-colors hover:bg-muted-foreground/50 cursor-pointer min-h-27.5`,
                i === currentPageIndex && 'bg-muted-foreground',
              )}
            >
              <img
                src={page.preview}
                alt={`Page ${page.number + 1}`}
                className="w-full rounded object-contain flex-1"
              />
              <span
                className={cn(
                  'text-xs text-muted-foreground group-hover:text-white transition-colors',
                  i === currentPageIndex && 'text-white',
                )}
              >
                {page.number + 1}
              </span>
            </button>
          ))}
    </aside>
  )
}
